/**
 * Per-card-type chip labels and per-status pill labels.
 * Spec: v2-design/CARD_SHELL_v0_spec.md §3.
 */
import { CardType, PlanStatus } from '../../types';

/** Tool-name labels for the type chip in the card header. */
export function cardTypeChipLabel(
  cardType: CardType,
  content: Record<string, unknown>
): string {
  switch (cardType) {
    case 'plan_confirmation_card':
      return toolLabel(content.tool);
    case 'chart_visualization_param_card':
      return 'Chart Visualization';
    case 'driver_analysis_param_card':
      return 'Driver Analysis';
    case 'edit_scenarios_input_selection_card':
      return 'Edit Scenarios';
    case 'edit_scenarios_change_review_card':
      return 'Edit Scenarios';
    case 'extracts_param_card':
      return 'Extracts';
    case 'scope_proposal_card':
      return 'Scope';
    case 'ambiguity_card':
      return 'Clarification';
    case 'result_card':
      return toolLabel(content.tool) || 'Result';
    case 'explain_answer_card':
      return 'Answer';
    default:
      return cardType;
  }
}

function toolLabel(tool: unknown): string {
  switch (String(tool ?? '')) {
    case 'edit_scenarios':
      return 'Edit Scenarios';
    case 'chart_visualization':
      return 'Chart Visualization';
    case 'driver_analysis':
      return 'Driver Analysis';
    case 'extracts':
      return 'Extracts';
    default:
      return '';
  }
}

export function cardTypeSubtitle(cardType: CardType): string | null {
  switch (cardType) {
    case 'edit_scenarios_input_selection_card':
      return 'Parameters';
    case 'edit_scenarios_change_review_card':
      return 'Apply changes';
    default:
      return null;
  }
}

export type StatusPillTone = 'info' | 'neutral' | 'success' | 'warning';

export interface StatusPillSpec {
  label: string;
  tone: StatusPillTone;
}

/**
 * What the right-aligned status pill in the card header says.
 *
 * Inputs:
 *   - cardType: which card we're rendering
 *   - planStatus: the plan's current status (null/undefined for non-plan cards)
 *   - resolved: local UI state for cards the user has just resolved
 */
export function statusPillSpec(
  cardType: CardType,
  planStatus: PlanStatus | null | undefined,
  resolved: 'confirmed' | 'cancelled' | 'edited' | 'amended' | undefined
): StatusPillSpec {
  // Resolved (past-state) labels take precedence.
  if (resolved === 'cancelled') {
    return { label: 'Cancelled', tone: 'neutral' };
  }
  if (resolved === 'amended') {
    return { label: 'Superseded by amendment', tone: 'neutral' };
  }
  if (resolved === 'edited') {
    return { label: 'Edited', tone: 'neutral' };
  }
  if (resolved === 'confirmed') {
    return { label: 'Confirmed', tone: 'neutral' };
  }

  // Non-plan cards: card type drives the label.
  if (cardType === 'scope_proposal_card') {
    return { label: 'Suggested change', tone: 'info' };
  }
  if (cardType === 'ambiguity_card') {
    return { label: 'Clarification', tone: 'warning' };
  }
  if (cardType === 'explain_answer_card') {
    return { label: 'Answer', tone: 'neutral' };
  }
  if (cardType === 'result_card') {
    return { label: 'Completed', tone: 'success' };
  }

  // Plan-bound cards: status drives.
  switch (planStatus) {
    case 'pending_plan_confirmation':
      return { label: 'Confirm plan', tone: 'info' };
    case 'pending_param_confirmation':
      return { label: 'Confirm parameters', tone: 'info' };
    case 'pending_diff_confirmation':
      return { label: 'Confirm changes', tone: 'info' };
    case 'executing':
      return { label: 'Processing…', tone: 'info' };
    case 'completed':
      return { label: 'Completed', tone: 'success' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'neutral' };
    case 'superseded':
      return { label: 'Superseded', tone: 'neutral' };
    default:
      return { label: '', tone: 'neutral' };
  }
}

/** Which expected_status to send on POST /v2/cards/resolve per card type. */
export function expectedStatusForCard(cardType: CardType): PlanStatus | null {
  switch (cardType) {
    case 'plan_confirmation_card':
      return 'pending_plan_confirmation';
    case 'edit_scenarios_input_selection_card':
    case 'driver_analysis_param_card':
    case 'chart_visualization_param_card':
    case 'extracts_param_card':
      return 'pending_param_confirmation';
    case 'edit_scenarios_change_review_card':
      return 'pending_diff_confirmation';
    case 'scope_proposal_card':
    case 'ambiguity_card':
    case 'result_card':
    case 'explain_answer_card':
      return null;
    default:
      return null;
  }
}

/**
 * Which cards use the amend semantic (transition back to a prior gate)
 * instead of edit_field (per-control inline patch at the same gate).
 */
export function cardUsesAmend(_cardType: CardType): boolean {
  // Amend path (transition back to a prior gate) is not supported in
  // v0. User feedback on any card re-routes through the Enumerator as
  // a new plan with parent_plan_id.
  return false;
}
